import React from 'react';
import {noop} from 'lodash';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom, type AtomRenderProps} from '@packages/systems/wfdl';
import {
  px,
  percent,
  url,
  twoValueSize,
} from '@packages/systems/core/utils/WebflowCssSubset';
import {
  FormCustomBooleanInput as FormCustomCheckboxInput,
  customControlBaseStyle,
  customControlFocusStyle,
} from './CustomControls';
import {setAllBordersRadius, setAllBordersColor} from './utils';
import {paths} from '@packages/systems/core/constants/SharedConfig';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {usePreviewMode} from '@packages/systems/render';
import {getInPojo, getString} from '@packages/utilities/data-utils';

const {CUSTOM_CHECKBOX_CHECK_URL} = paths;

const FormCheckboxInput = ({
  attributes,
  classes,
  generateAttributes,
  elementId,
}: AtomRenderProps) => {
  const props = generateAttributes(attributes, classes);
  const previewMode = usePreviewMode();

  const checked = Boolean(props.checked);
  let finalProps;
  // We are using "key" on this input so we can switch between controlled and
  // uncontrolled for the same input. The different key causes a new instance to be
  // created on render, rather than using the existing one.
  if (previewMode) {
    finalProps = {
      ...props,
      defaultChecked: checked,
      checked: undefined,
      key: elementId + '-preview',
    };
  } else {
    finalProps = {
      ...props,
      checked,
      onChange: noop,
      key: elementId,
    };
  }
  return <input {...finalProps} />;
};

const render = Atom.createRender((props) =>
  getString(getInPojo(props.data, ['inputType'])) === 'custom' ? (
    <FormCustomCheckboxInput isCheckbox {...props} />
  ) : (
    <FormCheckboxInput {...props} />
  )
);

const config = Atom.create({
  className: 'w-checkbox-input',
  displayName: 'Checkbox',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('Checkbox')),
  },
  showEmpty: false,
  constraints: {
    pinToParent: true,
  },
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Form'), typeName('FormCheckbox'))
  ),
  render,
  style: {
    base: {
      main: {
        noPseudo: {
          float: 'left',
          marginBottom: px(0),
          marginLeft: px(-20),
          marginRight: px(0),
          marginTop: px(4),
          lineHeight: 'normal',
        },
      },
    },
    conditional: {
      inputType: {
        custom: {
          // Technically this could be fixed by updating the
          // `AtomStyleDeclaration` type to allow
          // `$Values<typeof REDIRECTED_PSEUDOS>` in addition to
          // `PseudoStateKey`s, however since `FormRadio/CheckboxInput`
          // is the only atom that actually supports these we're just
          // ignoring the error here.
          //

          main: {
            noPseudo: {
              ...customControlBaseStyle,
              ...setAllBordersRadius(px(2)),
            },
            // @ts-expect-error - TS2322 - Type '{ noPseudo: { borderBottomLeftRadius: string; borderBottomRightRadius: string; borderTopLeftRadius: string; borderTopRightRadius: string; width: string; height: string; borderTopStyle: "none" | ... 2 more ... | "solid"; ... 10 more ...; borderRightWidth: string; }; 'redirected-checked': { ...; }; 'redirected-focus':...' is not assignable to type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>>'.
            'redirected-checked': {
              backgroundColor: {r: 56, g: 152, b: 236, a: 1},
              ...setAllBordersColor({r: 56, g: 152, b: 236, a: 1}),
              backgroundImage: url(CUSTOM_CHECKBOX_CHECK_URL),
              backgroundPosition: twoValueSize(percent(50), percent(50)),
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
            },
            'redirected-focus': {
              ...customControlFocusStyle,
            },
          },
        },
      },
    },
  },
  hideAncestorSettings: true,
});

export default config;
